/**
 * Consent, ported from the design's residentsConsentAndNotes() (L30091-30497): two grants,
 * both off until someone says yes; the network of who feeds the record and who reads it;
 * the Reach grid; the append-only history; a rehearsal of a withdrawal that writes nothing.
 * The residents and their two grants come from GET /residents/consent-overview, the network
 * from GET /residents/:id/data-flows (both served), the history from GET /consent (served).
 */
import type { ConsentOverviewResponse, ConsentRecord, DataFlowParty, DataFlowsResponse } from '@aithora/shared';
import { pre } from './dom';

type Ev = { preventDefault?: () => void } | undefined;
type G = 'on' | 'off' | 'exp' | 'wd';
const INK = '#22272E', MUTE = '#54606C', BLUE = '#2F5C97', RED = '#B3261E', AMBER = '#4A5866', OK = '#1F6B52', VIO = '#5B6B7C';
const TONE: Record<string, string> = { '': INK, red: RED, amber: AMBER, ok: OK, mute: MUTE, blue: BLUE, vio: VIO };
const ease = 'cubic-bezier(.22,.61,.36,1)';
const mark = (tone: string) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:16px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' +
  (TONE[tone] || MUTE) + ';background:' + (TONE[tone] || MUTE) + '1A;';
// 'aiInteraction' is the data-collection grant the server enforces (src/aithora/consent.ts).
const GRANTS: Array<[string, 'aiInteraction' | 'model_training', string]> = [
  ['AI data collection', 'aiInteraction', 'Lets Atlas talk with her and keep what she says.'],
  ['Model training', 'model_training', 'Lets her de-identified conversations improve the models.'],
];
const MK: Record<G, [string, string, string]> = { on: ['●', 'ok', 'granted'], off: ['○', 'mute', 'never asked'], exp: ['◆', 'amber', 'lapsing'], wd: ['▲', 'red', 'withdrawn'] };
const OUTSIDE: Record<string, 1> = { 'Training corpus': 1, Research: 1, Payer: 1 };
const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dm = (iso: string) => { const d = new Date(iso); return d.getDate() + ' ' + MONS[d.getMonth()]; };

export interface ConsentUi {
  sel: string | null;
  view: 'net' | 'grid' | 'ledger';
  sim: 0 | 1 | null;
  asOf: number | null;
  focus: string | null;
  pend: string | null;
  playing: boolean;
  /** The last withdrawal this session sent, with the propagation the server reported. */
  prop: { key: string; steps: Array<{ badge: string; tone: string; what: string; t: string }> } | null;
  /** The consent receipt is open. */
  receipt?: boolean;
}

export function defaultResident(res: ConsentOverviewResponse['residents']): string | null {
  // Open on the resident whose record reaches furthest: most grants held, then list order.
  let best: string | null = null;
  let most = -1;
  for (const r of res) {
    const n = (r.collection === 'on' || r.collection === 'exp' ? 1 : 0) + (r.training === 'on' ? 1 : 0);
    if (n > most) { most = n; best = r.residentId; }
  }
  return best;
}

export function consentVals(opts: {
  residents: ConsentOverviewResponse['residents'];
  flows: DataFlowsResponse | null;
  records: ConsentRecord[];
  ui: ConsentUi;
  setUi: (p: Partial<ConsentUi>) => void;
  canWrite: boolean;
  grant: (residentId: string, i: 0 | 1, on: boolean) => void;
  cell: (recipient: string, ci: number, shared: boolean) => void;
  togglePlay: () => void;
  openException: () => void;
}) {
  const { residents: RES, flows, ui, setUi } = opts;
  const quarters = flows?.quarters ?? ['now'];
  const nowIx = quarters.length - 1;
  const asOf = ui.asOf == null ? nowIx : ui.asOf;
  const past = asOf < nowIx;
  const GAT = flows?.grantQuarters ?? [0, 0];
  const selR = RES.find((r) => r.residentId === ui.sel) ?? RES.find((r) => r.residentId === defaultResident(RES)) ?? null;
  const gState = (r: (typeof RES)[number], i: number): G => (i === 0 ? r.collection : r.training);
  const simG = ui.sim;
  const simOn = simG !== null;
  const simOf = (r: (typeof RES)[number], i: number): G => (simOn && selR && r.residentId === selR.residentId && i === simG ? 'wd' : gState(r, i));
  const eff = (r: (typeof RES)[number], i: number): G => (asOf < GAT[i] ? 'off' : simOf(r, i));
  const collectOn = !!selR && (eff(selR, 0) === 'on' || eff(selR, 0) === 'exp');
  const trainOn = !!selR && eff(selR, 1) === 'on';
  const live = (p: DataFlowParty) => (p.gate === 'ai_data_collection' ? collectOn : p.gate === 'model_training' ? trainOn : true);
  type Row = [string, string, boolean, number, string, number[], number[] | undefined];
  const toRow = (p: DataFlowParty): Row => [p.name, p.basis, live(p), p.share, p.glyph, p.history, p.classes];
  const SRC = (flows?.sources ?? []).map(toRow);
  const RCP = (flows?.recipients ?? []).map(toRow);
  const selName = selR?.name ?? '–';
  const selRoom = selR?.room ?? '–';

  // ---- graph measures (design: neigh, concentration, downstream, cascadeLevels, fragility) ----
  const EDGES = SRC.map((x) => ({ s: x[0], c: selName, w: x[3], live: x[2] })).concat(RCP.map((x) => ({ s: selName, c: x[0], w: x[3], live: x[2] })));
  const neigh = (f: string) => ({
    sup: EDGES.filter((e) => e.c === f && e.live).sort((a, b) => b.w - a.w),
    cus: EDGES.filter((e) => e.s === f && e.live).sort((a, b) => b.w - a.w),
  });
  const concentration = (f: string) => {
    const ng = neigh(f);
    let tot = 0;
    ng.cus.forEach((e) => { tot += e.w; });
    if (!tot) return null;
    let hhi = 0;
    const top = { id: null as string | null, share: 0 };
    ng.cus.forEach((e) => { const sh = e.w / tot; hhi += sh * sh; if (sh > top.share) { top.share = sh; top.id = e.c; } });
    hhi = Math.round(hhi * 10000);
    return { hhi, band: hhi >= 2500 ? 'high' : hhi >= 1500 ? 'moderate' : 'low', topId: top.id, topShare: Math.round(top.share * 100), spof: top.share >= 0.3 };
  };
  const downstream = (f: string) => {
    const seen: Record<string, 1> = {};
    const stack = [f];
    while (stack.length) {
      const cur = stack.pop() as string;
      if (seen[cur]) continue;
      seen[cur] = 1;
      EDGES.forEach((e) => { if (e.s === cur && e.live && !seen[e.c]) stack.push(e.c); });
    }
    return seen;
  };
  const cascadeLevels = (src: string) => {
    const lvl: Record<string, number> = { [src]: 0 };
    const q = [src];
    while (q.length) {
      const cur = q.shift() as string;
      EDGES.forEach((e) => { if (e.s === cur && e.live && lvl[e.c] === undefined) { lvl[e.c] = lvl[cur] + 1; q.push(e.c); } });
    }
    return lvl;
  };
  const outsideShare = (f: string) => {
    const ng = neigh(f);
    let tot = 0, out = 0;
    ng.cus.forEach((e) => { tot += e.w; if (OUTSIDE[e.c]) out += e.w; });
    return tot ? Math.round((out / tot) * 100) : 0;
  };
  const conc = concentration(selName);
  const reachN = Object.keys(downstream(selName)).length - 1;
  const fragility = (() => {
    if (!conc) {
      return { score: 4, band: 'contained', factors: [{ k: 'Concentration', v: 0, d: 'nothing read' }, { k: 'Single-point', v: 0, d: 'none' }, { k: 'Beyond the building', v: 0, d: 'nothing leaves' }, { k: 'Sources feeding it', v: 0, d: 'collection off' }] };
    }
    const fConc = Math.min(100, conc.hhi / 45);
    const fSpof = Math.min(100, conc.topShare * 1.4);
    const fOut = outsideShare(selName);
    const srcLive = neigh(selName).sup;
    let sw = 0;
    srcLive.forEach((e) => { sw += e.w; });
    const fSrc = Math.min(100, sw);
    const score = Math.round(fConc * 0.34 + fSpof * 0.3 + fOut * 0.22 + fSrc * 0.14);
    return {
      score, band: score >= 66 ? 'critical' : score >= 40 ? 'elevated' : 'contained',
      factors: [
        { k: 'Concentration', v: Math.round(fConc), d: 'HHI ' + conc.hhi },
        { k: 'Single-point', v: Math.round(fSpof), d: conc.topId + ' ' + conc.topShare + '%' },
        { k: 'Beyond the building', v: fOut, d: fOut ? fOut + '% of reads' : 'nothing leaves' },
        { k: 'Sources feeding it', v: Math.round(fSrc), d: srcLive.length + ' of ' + SRC.length + ' live' },
      ],
    };
  })();
  const reachScore = (() => {
    const out = outsideShare(selName);
    const lvl = cascadeLevels(selName);
    const hops = Math.max(0, ...Object.keys(lvl).map((k) => lvl[k]));
    const score = Math.round(Math.min(100, out * 0.5 + (reachN / Math.max(1, RCP.length)) * 100 * 0.35 + hops * 8));
    return {
      score, band: out > 0 ? 'leaves the building' : 'stays inside',
      factors: [
        { k: 'Reads her record', v: Math.round((reachN / Math.max(1, RCP.length)) * 100), d: reachN + ' of ' + RCP.length + ' recipients' },
        { k: 'Beyond the building', v: out, d: out ? out + '% of reads' : 'none' },
        { k: 'Hops from her', v: Math.min(100, hops * 33), d: hops + (hops === 1 ? ' hop' : ' hops') },
        { k: 'Blocked by consent', v: Math.round((RCP.filter((x) => !x[2]).length / Math.max(1, RCP.length)) * 100), d: RCP.filter((x) => !x[2]).length + ' refused at the gate' },
      ],
    };
  })();

  // ---- layout ----
  // The lowest node sits well above the box's bottom edge so its two line label always fits inside it.
  const CX = 330, CY = 170;
  const lay = (arr: Row[], x: number) => arr.map((r, i) => ({ r, x, y: 50 + i * ((290 - 50) / Math.max(1, arr.length - 1)) }));
  const srcN = lay(SRC, 126);
  const rcpN = lay(RCP, 550);
  type N = ReturnType<typeof lay>[number];
  const nodeOn = (n: N) => n.r[2];
  const edge = (a: { x: number; y: number }, b: { x: number; y: number }, isLive: boolean, w: number, k: 'in' | 'out') => {
    const mx = (a.x + b.x) / 2;
    return {
      d: 'M' + a.x + ' ' + a.y + ' C ' + mx + ' ' + a.y + ', ' + mx + ' ' + b.y + ', ' + b.x + ' ' + b.y,
      stroke: isLive ? (k === 'in' ? 'rgba(47,92,151,.42)' : 'rgba(31,107,82,.42)') : simOn ? 'rgba(74,88,102,.40)' : 'rgba(34,39,46,.16)',
      w: isLive ? String(w) : '1.4', dash: isLive ? 'none' : simOn ? '2 5' : '4 4', op: isLive ? '1' : simOn ? '.9' : '.85',
      dotStroke: isLive ? (k === 'in' ? BLUE : OK) : 'transparent', dotW: isLive ? '3.4' : '0',
      flowStyle: isLive ? 'animation:flowDash 1.1s linear infinite;' : 'display:none;',
    };
  };
  const rOf = (n: N) => 8 + Math.min(3.5, Math.sqrt(n.r[3]) * 0.6);
  const kEdges = srcN.map((n) => edge({ x: n.x + rOf(n) + 2, y: n.y }, { x: CX - 38, y: CY }, nodeOn(n), 1 + Math.min(4.4, n.r[3] * 0.11), 'in'))
    .concat(rcpN.map((n) => edge({ x: CX + 38, y: CY }, { x: n.x - rOf(n) - 2, y: n.y }, nodeOn(n), 1 + Math.min(4.4, n.r[3] * 0.11), 'out')));
  const mkNode = (n: N, kind: 'in' | 'out') => ({
    x: String(n.x), y: String(n.y), r: String(rOf(n)), fill: '#FBF9F4',
    stroke: nodeOn(n) ? (kind === 'in' ? BLUE : OK) : simOn ? AMBER : 'rgba(34,39,46,.22)', sw: nodeOn(n) ? '1.6' : simOn ? '2' : '1.4',
    label: n.r[0], sub: nodeOn(n) ? n.r[1] : simOn ? 'would go dark' : 'blocked', hasSub: kind === 'in' || !nodeOn(n),
    gStyle: 'animation:popIn .32s cubic-bezier(.22,.61,.36,1) both;',
    glyph: n.r[4],
    glyphStyle: 'position:absolute;left:' + ((n.x / 660) * 100).toFixed(2) + '%;top:' + ((n.y / 360) * 100).toFixed(2) + '%;transform:translate(-50%,-50%);font-size:14px;font-weight:800;letter-spacing:.03em;pointer-events:none;color:' + (nodeOn(n) ? (kind === 'in' ? BLUE : OK) : MUTE) + ';',
    labStyle: 'position:absolute;left:' + ((n.x / 660) * 100).toFixed(2) + '%;top:' + (((n.y + rOf(n) + 4) / 360) * 100).toFixed(2) + '%;transform:translateX(-50%);text-align:center;white-space:nowrap;pointer-events:none;animation:fadeIn .32s cubic-bezier(.22,.61,.36,1) both;',
    labInk: 'font-size:14px;font-weight:700;line-height:1.15;color:' + (nodeOn(n) ? INK : MUTE) + ';',
    subInk: 'font-size:14px;color:#54606C;line-height:1.15;margin-left:5px;',
  });
  const kNodes = srcN.map((n) => mkNode(n, 'in')).concat(rcpN.map((n) => mkNode(n, 'out'))).concat([{
    x: String(CX), y: String(CY), r: '30', fill: '#FCFAF4', stroke: BLUE, sw: '2.4', label: selName, sub: 'room ' + selRoom, hasSub: false, // the design's hub leaves hasSub unset, so its room line never shows
    gStyle: 'animation:popIn .32s cubic-bezier(.22,.61,.36,1) both;', glyph: '', glyphStyle: 'display:none;',
    labStyle: 'position:absolute;left:' + ((CX / 660) * 100).toFixed(2) + '%;top:' + (((CY - 16) / 360) * 100).toFixed(2) + '%;transform:translateX(-50%);text-align:center;white-space:nowrap;pointer-events:none;',
    labInk: 'font-size:14px;font-weight:700;letter-spacing:-.01em;line-height:1.2;color:' + INK + ';',
    subInk: 'display:block;font-size:14px;color:#54606C;line-height:1.2;',
  }]);
  const kHits = srcN.concat(rcpN).map((n) => ({ x: String(n.x), y: String(n.y), onClick: (e: Ev) => { pre(e); setUi({ focus: n.r[0] }); } }));
  const CLASSES = flows?.classes ?? [];
  const revoked = new Set(flows?.revokedCells ?? []);
  // A view switch: the one on is marked selected (pressing it again keeps it).
  const tabBtn = (k: ConsentUi['view'], label: string) => ({
    label,
    on: ui.view === k,
    onClick: (e: Ev) => { pre(e); setUi({ view: k }); },
    style: 'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (ui.view === k ? 'background:#FCFAF4;color:' + INK + ';box-shadow:0 1px 2px rgba(34,39,46,.12);cursor:default;' : 'color:' + MUTE + ';'),
  });

  // ---- history: the served consent records, and the events between them ----
  const recRows = opts.records
    .filter((r) => selR && r.user_id === selR.residentId)
    .flatMap((r) => {
      const g = GRANTS.find((x) => x[1] === r.consent_type);
      const label = g ? g[0] : r.consent_type;
      const rows: Array<{ at: string; badge: string; tone: string; what: string; seal: string | null }> = [
        r.granted
          ? { at: r.created_at, badge: 'GRANTED', tone: 'ok', what: label + ', granted by ' + (selR?.grantedBy ?? 'the resident'), seal: r.ledgerRef ?? null }
          : { at: r.created_at, badge: 'DECLINED', tone: 'mute', what: label + ', offered and declined', seal: r.ledgerRef ?? null },
      ];
      if (r.revoked_at) rows.push({ at: r.revoked_at, badge: 'WITHDRAWN', tone: 'red', what: label + ', withdrawn', seal: r.ledgerRef ?? null });
      return rows;
    });
  const ledgerAll = recRows.concat(flows?.events ?? []).sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const qIndexOf = (at: string) => {
    // the quarter an entry falls in, from the end ("now" is the last)
    const d = new Date(at);
    const now = new Date();
    const back = (now.getFullYear() - d.getFullYear()) * 4 + (Math.floor(now.getMonth() / 3) - Math.floor(d.getMonth() / 3));
    return Math.max(0, nowIx - back);
  };
  const ledger = ledgerAll.filter((h) => !past || qIndexOf(h.at) <= asOf);

  const selPills: Array<[string, string]> = [
    [(collectOn ? 1 : 0) + (trainOn ? 1 : 0) + ' held', collectOn ? 'ok' : 'red'],
    [SRC.filter((x) => x[2]).length + ' sources', 'blue'],
    [RCP.filter((x) => x[2]).length + ' recipients', 'blue'],
    [RCP.filter((x) => !x[2]).length + ' blocked', RCP.filter((x) => !x[2]).length ? 'amber' : 'mute'],
  ];
  const panels = [
    { key: 'EXPOSURE', n: fragility.score, band: fragility.band.toUpperCase(), tone: fragility.band === 'critical' ? 'red' : fragility.band === 'elevated' ? 'amber' : 'ok', rows: fragility.factors },
    { key: 'REACH', n: reachScore.score, band: reachScore.band.toUpperCase(), tone: reachScore.band === 'leaves the building' ? 'amber' : 'ok', rows: reachScore.factors },
  ];
  const propSteps = simOn
    ? simG === 1
      ? [['WOULD STOP', 'amber', 'Her turns leave the corpus at the next build', 'overnight'], ['WOULD KEEP', 'ok', 'Atlas and her care are untouched', 'immediately'], ['CANNOT', 'mute', 'Models already trained are not retrained', 'never']]
      : [['WOULD STOP', 'amber', 'Atlas goes dark and she is told so', 'instant'], ['WOULD STOP', 'amber', 'Her circle stops receiving updates', '2s'], ['WOULD QUEUE', 'amber', 'The training corpus rebuilds overnight', 'overnight']]
    : (ui.prop?.steps ?? []).map((s) => [s.badge, s.tone, s.what, s.t]);
  const propFailed = !simOn && !!ui.prop && ui.prop.steps.some((s) => s.tone === 'red');
  const fullSeries = [1, 1, 1, 2, 2, 2, (collectOn ? 1 : 0) + (trainOn ? 1 : 0)];

  return {
    kGate: '● FAILS CLOSED',
    kGateStyle: mark('ok'),
    kCounts: RES.length + ' residents · ' + (SRC.length + RCP.length) + ' parties · ' + RES.length * 2 + ' grants',
    kLede: 'Two consents, both off until someone says yes. Nothing is inferred from silence.',
    kViews: [tabBtn('net', 'Network'), tabBtn('grid', 'Reach'), tabBtn('ledger', 'History')],
    kIsNet: ui.view === 'net', kIsGrid: ui.view === 'grid', kIsLedger: ui.view === 'ledger',
    kStageNote: simOn ? '◆ WHAT-IF · nothing has been signed' : past ? 'as it stood in ' + quarters[asOf] : collectOn ? 'live · ' + SRC.filter((x) => x[2]).length + ' in, ' + RCP.filter((x) => x[2]).length + ' out' : 'collection off · Atlas is dark',
    kEdges, kNodes, kHits,
    kLegend: ([['Feeds her record', BLUE], ['Reads from it', OK], ['Blocked by consent', MUTE]] as const).map((l) => ({ label: l[0], dot: 'width:7px;height:7px;border-radius:50%;background:' + l[1] + ';display:inline-block;' })),
    kChips: RES.map((r) => ({
      label: r.name,
      n: ((eff(r, 0) === 'on' || eff(r, 0) === 'exp') ? 1 : 0) + (eff(r, 1) === 'on' ? 1 : 0) + '/2',
      // One resident is shown at a time: the chip is a choice, and picking one also leaves a
      // recipient that was in focus, so the panel always shows the resident pressed.
      on: !!selR && selR.residentId === r.residentId && !ui.focus,
      onClick: (e: Ev) => { pre(e); setUi({ sel: r.residentId, pend: null, focus: null }); },
      dotStyle: 'width:6px;height:6px;border-radius:50%;background:' + (TONE[MK[eff(r, 0)][1]] || MUTE) + ';display:inline-block;',
      style: 'display:inline-flex;align-items:center;gap:6px;height:27px;padding:0 10px;border-radius:14px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' +
        (selR && selR.residentId === r.residentId ? 'background:rgba(47,92,151,.13);color:#1D3F6B;' : 'background:rgba(34,39,46,.05);color:' + MUTE + ';'),
    })),
    kSimToggle: (e: Ev) => { pre(e); setUi({ sim: simG === null ? 0 : simG === 0 ? 1 : null, pend: null }); },
    kSimLabel: simG === 0 ? 'What if: training instead' : simG === 1 ? 'Stop the rehearsal' : 'Rehearse a withdrawal',
    kSimStyle: 'display:inline-flex;align-items:center;height:27px;padding:0 12px;border-radius:14px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (simOn ? 'background:rgba(74,88,102,.14);color:' + AMBER + ';' : 'background:rgba(34,39,46,.05);color:' + MUTE + ';'),
    kGridCols: 'display:grid;grid-template-columns:minmax(140px,1.6fr) repeat(' + CLASSES.length + ',minmax(58px,1fr));gap:9px;align-items:end;padding-bottom:7px;min-width:0;',
    kClasses: CLASSES,
    kGridRows: RCP.map((rc) => ({
      name: rc[0],
      sub: rc[2] ? 'reads under ' + rc[1] : 'blocked · ' + rc[1] + ' is off',
      rowStyle: 'display:grid;grid-template-columns:minmax(140px,1.6fr) repeat(' + CLASSES.length + ',minmax(58px,1fr));gap:9px;align-items:center;padding:8px 0;border-top:1px solid rgba(34,39,46,.12);min-width:0;' + (rc[2] ? '' : 'opacity:.5;'),
      cells: (rc[6] ?? []).map((v, ci) => {
        const off = revoked.has(rc[0] + ':' + ci);
        const isLive = rc[2] && v === 1 && !off;
        const never = v === 0;
        return {
          mark: never ? '·' : isLive ? '●' : '○',
          title: never ? CLASSES[ci] + ' is never shared with ' + rc[0] : isLive ? 'Revoke ' + CLASSES[ci] + ' for ' + rc[0] : CLASSES[ci] + ' is off for ' + rc[0],
          onClick: (e: Ev) => { pre(e); if (never || !rc[2] || !opts.canWrite) return; opts.cell(rc[0], ci, off); },
          style: 'display:flex;align-items:center;justify-content:center;height:26px;border-radius:6px;text-decoration:none;font-size:16px;' +
            (never ? 'color:rgba(34,39,46,.2);cursor:default;' : isLive ? 'color:' + OK + ';background:rgba(31,107,82,.1);' : 'color:' + RED + ';background:rgba(179,38,30,.08);'),
        };
      }),
    })),
    kGridFoot: 'A cell is revocable on its own: you can stop sharing one data class with one recipient without touching the grant. A middot means that class is never shared with that recipient at all, so there is nothing to revoke.',
    kLedger: ledger.map((h) => ({
      when: dm(h.at), badge: h.badge, what: h.what, seal: h.seal ?? '–', badgeStyle: mark(h.tone),
      dot: 'flex:0 0 6px;width:6px;height:6px;border-radius:50%;align-self:center;background:' + (TONE[h.tone] || MUTE) + ';',
    })),
    kLedgerFoot: 'Append-only. A change is a new entry, never an edit, and the state above each grant is simply its most recent one.',
    kPropOn: propSteps.length > 0,
    kPropNote: simOn ? '◆ WHAT-IF · this is what would happen, in this order. Nothing is signed and no entry is written.' : 'a withdrawal is not one switch: it is several systems, and they answer at different speeds',
    kProp: propSteps.map((p) => ({ badge: p[0], badgeStyle: mark(p[1]), what: p[2], t: p[3], tColor: p[1] === 'red' ? RED : p[1] === 'amber' ? AMBER : OK })),
    kPropFailed: propFailed,
    kPropFixLabel: 'Open the exception',
    kPropFix: (e: Ev) => { pre(e); opts.openException(); },
    kReliedOn: propSteps.length > 0,
    kReliedLede: (simOn ? 'Even if she withdrew today: ' : '') + 'withdrawing stops what happens next. It does not recall what already left, and this console will not pretend otherwise.',
    kRelied: flows?.reliedOn ?? [],
    kAsOf: quarters[asOf] ?? 'now',
    kTicks: quarters.map((q, i) => ({
      title: q,
      onClick: (e: Ev) => { pre(e); setUi({ asOf: i }); },
      style: 'flex:1 1 0;min-width:6px;height:5px;border-radius:3px;text-decoration:none;background:' + (i <= asOf ? BLUE : 'rgba(34,39,46,.14)') + ';',
    })),
    kFirstQ: (quarters[0] ?? '').replace(' ', '’'),
    kMidQ: (quarters[Math.min(3, nowIx)] ?? '').replace(' ', '’'),
    kPlayGlyph: ui.playing ? '■' : '▶',
    kPlayTitle: ui.playing ? 'Stop' : 'Play the history forward',
    kPlayStyle: 'display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:6px;font-size:14px;text-decoration:none;' +
      (ui.playing ? 'background:rgba(47,92,151,.14);color:#1D3F6B;' : 'background:rgba(34,39,46,.05);color:' + MUTE + ';'),
    kPlay: (e: Ev) => { pre(e); opts.togglePlay(); },
    kNow: (e: Ev) => { pre(e); setUi({ asOf: null }); },
    kNowStyle: 'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (asOf === nowIx ? 'background:rgba(47,92,151,.13);color:#1D3F6B;' : 'color:' + MUTE + ';background:rgba(34,39,46,.05);'),
    kSelDot: 'width:7px;height:7px;border-radius:50%;display:inline-block;background:' + (collectOn ? OK : MUTE) + ';',
    kSelBig: (collectOn ? 1 : 0) + (trainOn ? 1 : 0) + ' of 2',
    kSelBigLbl: 'consents held',
    kSelSpark: fullSeries.map((v, i) => (i ? 'L' : 'M') + (2 + (i / (fullSeries.length - 1)) * 86).toFixed(1) + ' ' + (25 - (v / 2) * 22).toFixed(1)).join(' '),
    kSelSparkC: collectOn ? OK : RED,
    kSelPills: selPills.map((p) => ({ label: p[0], style: mark(p[1]) })),
    kPanels: panels.map((p) => ({
      wrap: 'margin-top:11px;padding-top:10px;border-top:1px solid rgba(34,39,46,.12);min-width:0;',
      n: String(p.n), ringLbl: p.key, modStyle: mark('mute'),
      ring: 'flex:0 0 58px;width:58px;height:58px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:conic-gradient(' + (TONE[p.tone] || BLUE) + ' ' + p.n * 3.6 +
        'deg, rgba(34,39,46,.10) 0);transition:background .4s ' + ease + ';animation:popIn .4s ' + ease + ' both;',
      hole: 'width:44px;height:44px;border-radius:50%;background:#F6F3EC;display:flex;flex-direction:column;align-items:center;justify-content:center;font-style:normal;',
      numStyle: 'display:block;font-size:19px;font-weight:700;letter-spacing:-.02em;font-style:normal;line-height:1;color:' + (TONE[p.tone] || BLUE) + ';',
      band: p.band, bandStyle: mark(p.tone),
      rows: p.rows.map((f) => ({ k: f.k, d: f.d, bar: 'display:block;height:100%;border-radius:2px;width:' + f.v + '%;background:' + (f.v ? TONE[p.tone] || BLUE : 'rgba(34,39,46,.18)') + ';transform-origin:left;animation:growW .5s ' + ease + ' both;' })),
    })),
    kConcBand: conc ? conc.band.toUpperCase() : 'NOTHING READ',
    kConcBandStyle: mark(conc ? (conc.band === 'high' ? 'red' : conc.band === 'moderate' ? 'amber' : 'ok') : 'mute'),
    kConcMark: 'position:absolute;top:-3px;left:' + (conc ? Math.min(97, Math.round(conc.hhi / 100)) : 2) + '%;width:2px;height:11px;border-radius:1px;background:#22272E;transition:left .4s ' + ease + ';',
    kConcNote: !conc
      ? 'Nothing is being read, so there is no concentration to measure.'
      : conc.spof
        ? conc.topId + ' alone accounts for ' + conc.topShare + '% of what is read about her: a single point. HHI ' + conc.hhi + '.'
        : 'No single reader dominates. The largest, ' + conc.topId + ', accounts for ' + conc.topShare + '%. HHI ' + conc.hhi + '.',
    kLists: [{ title: 'Top sources', sub: 'share of her record', rows: SRC }, { title: 'Top recipients', sub: 'share of what is read', rows: RCP }].map((L) => ({
      title: L.title,
      sub: L.sub,
      rows: L.rows.slice().sort((a, b) => b[3] - a[3]).map((r) => ({
        name: r[0], pct: r[2] ? r[3] + '%' : '–', pctColor: r[2] ? INK : MUTE,
        dot: 'flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:' + (r[2] ? (L.title === 'Top sources' ? BLUE : OK) : MUTE) + ';',
        onClick: (e: Ev) => { pre(e); setUi({ focus: r[0] }); },
        style: 'display:flex;align-items:center;gap:7px;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:' + INK + ';' + (r[2] ? '' : 'opacity:.5;'),
        bars: r[5].slice(2).map((v, i) => 'display:block;width:3px;border-radius:1px;height:' + Math.max(2, Math.round((v / 48) * 13)) + 'px;background:' + (r[2] ? (L.title === 'Top sources' ? BLUE : OK) : MUTE) +
          ';opacity:' + (0.35 + i * 0.13).toFixed(2) + ';transform-origin:bottom;animation:growH .45s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 40 + 'ms;'),
      })),
    })),
    kSelKind: ui.focus ? 'Recipient' : 'Resident',
    kSelName: ui.focus || selName + ' · room ' + selRoom,
    kSelMeta: ui.focus ? 'reads from her record' : 'consents given by ' + (selR?.grantedBy ?? '–'),
    kSelGrants: selR ? GRANTS.map((g, i) => {
      const cs = eff(selR, i);
      const on = cs === 'on' || cs === 'exp';
      const key = selR.residentId + ':' + i;
      const btn = 'flex:0 0 auto;white-space:nowrap;display:inline-flex;align-items:center;height:29px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;';
      return {
        name: g[0],
        state: (past ? quarters[asOf] + ' · ' : '') + (cs === 'off' ? 'never asked' : cs === 'wd' ? 'withdrawn' : cs === 'exp' ? 'lapsing · by ' + selR.grantedBy : 'granted by ' + selR.grantedBy),
        onStyle: btn + (on ? 'background:' + OK + ';color:#FCFAF4;' : 'background:rgba(31,107,82,.10);color:' + OK + ';'),
        offStyle: btn + (!on ? 'background:' + RED + ';color:#FCFAF4;' : 'background:rgba(179,38,30,.10);color:' + RED + ';'),
        onOn: (e: Ev) => { pre(e); setUi({ asOf: null, pend: null }); if (!on && opts.canWrite) opts.grant(selR.residentId, i as 0 | 1, true); },
        onOff: (e: Ev) => { pre(e); setUi({ asOf: null }); if (on && opts.canWrite) setUi({ pend: ui.pend === key ? null : key }); },
        pending: ui.pend === key,
        blastTitle: 'Withdrawing ' + g[0].toLowerCase() + ' for ' + selName,
        blast: (i === 0
          ? [['STOPS', 'Atlas goes dark, and she is told so in her own words.'], ['KEEPS', 'Safety escalation and 911 stay live. Consent does not reach those.'], ['KEEPS', 'Everything already sealed stays sealed. Withdrawing is not deletion.']]
          : [['STOPS', 'Her turns leave the corpus at the next build.'], ['KEEPS', 'Nothing about her care changes. This grant only ever fed training.'], ['KEEPS', 'Models already trained are not retrained.']]).map((b) => ({ k: b[0], v: b[1] })),
        onConfirm: (e: Ev) => { pre(e); setUi({ pend: null }); opts.grant(selR.residentId, i as 0 | 1, false); },
        onCancel: (e: Ev) => { pre(e); setUi({ pend: null }); },
      };
    }) : [],
    // The receipt: the latest record of each grant, who witnessed it and its ledger seal.
    kReceipt: (e: Ev) => { pre(e); setUi({ receipt: !ui.receipt }); },
    kReceiptAction: ui.receipt ? 'Hide' : 'Show',
    kReceiptOpen: !!ui.receipt,
    kReceiptRows: GRANTS.map((g) => {
      const r = opts.records
        .filter((x) => selR && x.user_id === selR.residentId && x.consent_type === g[1])
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
      if (!r) return { k: g[0], v: 'No record' };
      const what = r.revoked_at ? 'Withdrawn ' + r.revoked_at.slice(0, 10) : (r.granted ? 'Granted ' : 'Declined ') + r.created_at.slice(0, 10);
      return { k: g[0], v: what + (r.witnessed_by ? ' · witnessed by ' + r.witnessed_by : '') + (r.ledgerRef ? ' · sealed ' + r.ledgerRef : ' · not sealed') };
    }),
  };
}
